import { z } from 'zod';

import type { PayrollAgreement } from '@/modules/payroll-agreement/public';
import { amortizationResultSchema } from '@/shared/amortization';

import { simulationProductSchema } from './simulation.wire';

// Fio dos consignados (legado: services/creditNote/creditNote.ts:709-739, services/datasets/datasets.ts:45-59,
// services/zetra/zetra.ts:56-69, services/creditProduct). Os tipos vêm dos read models do legado
// (`AmortizationReadModel`, `ISiapeMarginQueryReadModel`, `IZetraMarginQueryReadModel`), frouxos. Dinheiro em
// centavos, taxa em fração; a margem da Zetra vem em reais (`valorDisponivel`, Zetra.tsx:78, `* 100` no helpers:61).

const text = z.string().nullish();
const num = z.number().nullish();

export type PayrollAgreementItem = PayrollAgreement;

/** O produto do consignado (`CreditProductReadModel`): o que a simulação lê, mais a faixa de prazo. */
export const payrollProductSchema = simulationProductSchema.extend({
  minimumAmortizationPeriods: num,
  /** Fração; o cartão usa a taxa da simulação e, sem ela, esta. */
  monthlyInterest: num,
});
export type PayrollProduct = z.infer<typeof payrollProductSchema>;
export const payrollProductListSchema = z.array(payrollProductSchema);

/** Um item da resposta do `POST /Amortization/Batch` com id (`AmortizationReadModel`). */
export const payrollSimulationSchema = amortizationResultSchema.extend({
  productId: text,
  productName: text,
  /** Centavos: o valor da parcela pedido (cálculo por parcela). */
  requestedAmount: num,
  firstPaymentDate: text,
  termInMonths: num,
  /** Centavos: a comissão do cartão no modo compra (ProductCard.tsx:157-161). */
  comm: num,
});
export type PayrollSimulation = z.infer<typeof payrollSimulationSchema>;

/**
 * A resposta do Batch: um item por modelo, na ordem. Item sem `id` é erro (`isCodeError`, SimulateContainer.tsx:877);
 * o legado passa o item inteiro ao `handleOnErrorAmortizationSimulation`.
 */
export const payrollBatchResponseSchema = z.array(z.unknown());

/** O corpo de cada modelo (`QueryAmortizationCreateModel` + `AmortizationService.buildAmortizationModel`). */
export interface PayrollAmortizationModel {
  readonly amortizationType: string | null | undefined;
  readonly startDate: string;
  readonly termInMonths: number;
  readonly firstPaymentDate: string;
  readonly apr: number | null | undefined;
  readonly includePaymentFixedCosts: true;
  readonly financeTaxExempted: boolean | null | undefined;
  readonly requestedAmount: number;
  readonly calculateByValueType: 'Payment' | 'Liquid';
  readonly numberOfPayments: number;
  readonly calculationType: 'V360Meses' | 'V360DiasCorridos';
  readonly paymentPeriodicity: {
    readonly every: number;
    readonly periodicity: 'Monthly' | 'Daily';
  };
}

export interface PayrollBatchModel {
  readonly personId: null;
  readonly legalPerson: false;
  readonly registrationNumber: string;
  readonly productId: string;
  /**
   * Depois do primeiro Batch, o modelo leva o produto, o id da simulação e, em `amortization`, os campos da resposta
   * por cima (`onSuccess`, SimulateContainer.tsx:255-279): é isso que o "Editar proposta" reenvia.
   */
  readonly product?: PayrollProduct | undefined;
  readonly amortizationId?: unknown;
  readonly amortization: PayrollAmortizationModel;
}

// ─── Margem SIAPE (`ISiapeMarginQueryReadModel`, datasets.interface.ts:777-826) ────────────────────────────────

const siapeProductSchema = z.looseObject({
  CdRubrica: text,
  DsRubrica: text,
  CdConvenio: text,
  /** Centavos (`formatCurrencyInCents(VlMargemDisp)`, Siape.tsx:58). */
  VlMargemDisp: num,
});

const siapeLinkSchema = z.looseObject({
  CodOrgao: text,
  NmOrgao: text,
  CnpjOrgao: text,
  CdMatricula: text,
  OrgMatInst: text,
  CodSituacaoFuncional: text,
  DescSituacaoFuncional: text,
  Produtos: z.array(siapeProductSchema).nullish(),
});

export const siapeMarginSchema = z.looseObject({
  createdAt: text,
  result: z
    .looseObject({
      DtOperacao: text,
      Nome: text,
      CdRetCode: text,
      DsRetCode: text,
      VinculoFuncionals: z.array(siapeLinkSchema).nullish(),
    })
    .nullish(),
});
export type SiapeMargin = z.infer<typeof siapeMarginSchema>;
export const siapeMarginListSchema = z.array(siapeMarginSchema);

// ─── Margem Zetra (`IZetraMarginQueryReadModel`, ZetraMarginQueryReadModel.ts:5, generics.ts:1-204) ─────────────

const zetraMarginItemSchema = z.looseObject({
  codigo: text,
  descricao: text,
  /** Reais. */
  valorDisponivel: num,
});

const zetraServerSchema = z.looseObject({
  orgao: text,
  orgaoCodigo: text,
  matricula: text,
  margens: z.array(zetraMarginItemSchema).nullish(),
});

export const zetraMarginSchema = z.looseObject({
  createdAt: text,
  result: z
    .looseObject({
      mensagem: text,
      servidores: z.array(zetraServerSchema).nullish(),
    })
    .nullish(),
});
export type ZetraMargin = z.infer<typeof zetraMarginSchema>;
export const zetraMarginListSchema = z.array(zetraMarginSchema);
